/**
 * Turning the bytes of an uploaded CSV into text.
 *
 * Card #349: the importer used to read every file WITHOUT a UTF-8 byte-order
 * mark as Latin-1. Excel writes the mark, but Google Sheets, Numbers and most
 * other tools export UTF-8 without one -- so "Prénom" arrived as "PrÃ©nom",
 * in the column names the user maps AND in every imported value.
 *
 * UTF-8 is tried first and strictly (`fatal: true`): Latin-1 / Windows-1252
 * text with any accented letter is almost never valid UTF-8, so a strict
 * decode that fails is a reliable sign the file is the older encoding, and
 * only then do we fall back to it. Pure ASCII decodes the same either way.
 *
 * Known limit: the choice is for the WHOLE file. A file that mixes encodings
 * (UTF-8 rows pasted into a Latin-1 file, or a stray invalid byte) is read
 * as windows-1252 throughout, so its UTF-8 parts show as "Ã©" -- the same
 * result every BOM-less file got before. UTF-16 exports are not detected
 * (they never were).
 *
 * Kept as a plain function so it can be tested with real byte arrays.
 */
export function decodeCsvBytes(bytes: Uint8Array): string {
  const hasUtf8Bom = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  if (hasUtf8Bom) {
    // The mark says UTF-8; TextDecoder drops the mark itself.
    return new TextDecoder('utf-8').decode(bytes);
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    // Not valid UTF-8: an older export. windows-1252 is what browsers use for
    // "iso-8859-1" anyway, and it also covers € and curly quotes.
    return new TextDecoder('windows-1252').decode(bytes);
  }
}
